"use client";

import { createContext, useContext } from "react";
import {
  ColorKey,
  ColorPalette,
  Colors,
  ImportTheme,
  Theme,
  ThemeProvider,
} from "@template/theme";
import { useTypedSelector } from "@/redux/helpers/hooks.ts";
import { colorTypes } from "@/lib/helpers/generateDefaultShades.ts";
import generateShade from "@/lib/helpers/generateShade.ts";

const checkIfColorIsDark = (colorHex: string) => {
  // Convert hex to RGB
  let hex = colorHex.replace("#", "");
  let r = parseInt(hex.substr(0, 2), 16);
  let g = parseInt(hex.substr(2, 2), 16);
  let b = parseInt(hex.substr(4, 2), 16);

  // Calculate relative luminance (Y)
  // Formula: Y = 0.2126*R + 0.7152*G + 0.0722*B
  let relativeLuminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  // Check color brightness, return true if dark
  return relativeLuminance < 128;
};

export const checkIfDarkMode = (
  color: string,
  algorithm: Theme["color_algorithm"]
) => {
  switch (algorithm) {
    case "light":
      return false;
    case "dark":
      return true;
    case "auto":
    default:
      return checkIfColorIsDark(color);
  }
};

const StoreThemeContext = createContext<ImportTheme | null>(null);

export const StoreThemeContextProvider = ({ children }: { children?: any }) => {
  const storeTheme = useTypedSelector((state) => state.store.theme);
  const colors = storeTheme?.colors;
  const colorAlgorithm = storeTheme?.color_algorithm;
  const baseColor = colors?.base;

  const colorKeys = ["primary", "secondary", "success", "error", "default"];

  const themeColors = () =>
    colorKeys.reduce(
      (acc: ColorPalette, colorKey: ColorKey) => {
        const keyColors = colors[colorKey];
        const defaultColor = keyColors.default;

        const keysInKeyColors = Object.keys(keyColors);

        const necessaryColors = [...colorTypes];

        acc[colorKey] = necessaryColors.reduce(
          (acc, typeKey) => {
            const foundColor = colors[colorKey][typeKey];

            if (foundColor) {
              acc[typeKey] = foundColor;
            } else if (typeof typeKey === "number") {
              acc[typeKey] = generateShade({
                color: defaultColor,
                shade: typeKey,
                darkMode: checkIfDarkMode(baseColor, colorAlgorithm),
                baseColor,
              });
            }

            return acc;
          },
          {
            default: defaultColor,
          } as ColorPalette
        );

        return acc;
      },
      {
        outline: colors?.outline,
        base: baseColor,
      } as any as ColorPalette
    );

  if (!storeTheme.colors) return;

  const theme = {
    ...storeTheme,
    colors: themeColors(),
  } as any;

  return (
    <StoreThemeContext.Provider value={theme}>
      {children}
    </StoreThemeContext.Provider>
  );
};

const useStoreTheme = () => {
  const theme = useContext(StoreThemeContext);

  if (!theme) {
    throw new Error("useStoreTheme must be used within a StoreThemeProvider");
  }

  return theme;
};

export const StoreThemeProvider = ({ children }: { children?: any }) => {
  const theme = useStoreTheme();

  return (
    <StoreThemeContextProvider>
      <ThemeProvider
        theme={theme as any}
        type="import"
      >
        {children}
      </ThemeProvider>
    </StoreThemeContextProvider>
  );
};

export default useStoreTheme;
